import React from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { MapPin } from 'lucide-react';

/**
 * Simplified LocationSearch Component
 * 
 * Search for locations using Mapbox Geocoding API
 */
interface LocationSearchProps {
  onSelect?: (location: { lat: number; lng: number; name: string; address: string; city?: string; country?: string }) => void;
  initialLocation?: { lat: number; lng: number; name: string; address: string };
}

export const LocationSearch: React.FC<LocationSearchProps> = ({ onSelect, initialLocation }) => {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [searchResults, setSearchResults] = React.useState<Array<{
    id: string;
    text: string;
    place_name: string;
    center: [number, number];
    context?: Array<{
      id: string;
      text: string;
    }>;
  }>>([]);
  const [loading, setLoading] = React.useState(false);
  const [selectedLocation, setSelectedLocation] = React.useState(initialLocation || null);

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // Search for locations using Mapbox Geocoding API
  const searchLocations = async () => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    try {
      // Use Vite's import.meta.env instead of process.env for browser compatibility
      const mapboxToken = import.meta.env.VITE_MAPBOX_TOKEN || 'pk.placeholder.token';
      
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(searchQuery)}.json?access_token=${mapboxToken}&types=place,locality,neighborhood,address`
      );
      
      // Define types for the Mapbox API response (can be shared if used elsewhere)
      interface MapboxFeature {
        id: string;
        text: string;
        place_name: string;
        center: [number, number];
        context?: Array<{
          id: string;
          text: string;
        }>;
        // Add other properties from a feature if needed
      }

      interface MapboxGeocodingResponse {
        features: MapboxFeature[];
        // Add other top-level properties from the response if needed
      }

      const data = await response.json() as MapboxGeocodingResponse;
      setSearchResults(data.features || []);
    } catch (error) {
      console.error('Error searching locations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle location selection
  const handleSelectLocation = (feature: {
    id: string;
    text: string;
    place_name: string;
    center: [number, number];
    context?: Array<{
      id: string;
      text: string;
    }>;
  }) => {
    const location = {
      lat: feature.center[1],
      lng: feature.center[0],
      name: feature.place_name, // Add name property
      address: feature.place_name,
      city: feature.context?.find((c) => c.id.startsWith('place'))?.text,
      country: feature.context?.find((c) => c.id.startsWith('country'))?.text
    };
    
    setSelectedLocation(location);
    if (onSelect) {
      onSelect(location);
    }
    setSearchResults([]);
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <div className="relative flex-grow">
          <Input
            type="text"
            placeholder="Search for a location..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="w-full"
          />
          {searchResults.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white rounded-md shadow-lg max-h-60 overflow-auto">
              {searchResults.map((feature) => (
                <div
                  key={feature.id}
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => handleSelectLocation(feature)}
                >
                  <div className="font-medium">{feature.text}</div>
                  <div className="text-sm text-gray-500">{feature.place_name}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        <Button 
          type="button" 
          onClick={searchLocations}
          disabled={loading}
        >
          {loading ? 'Searching...' : 'Search'}
        </Button>
      </div>
      
      {selectedLocation && (
        <div className="flex items-center p-2 bg-gray-50 rounded-md">
          <MapPin className="h-4 w-4 mr-2 text-gray-500" />
          <span className="text-sm">{selectedLocation.address}</span>
        </div>
      )}
    </div>
  );
};

export default LocationSearch;
